import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Shield, AlertTriangle, Eye } from 'lucide-react';
import { adminApi } from '@/admin/services/api';
import { toast } from 'sonner';

interface SecurityAlert {
  id: string;
  type: string;
  message: string;
  timestamp: string;
  resolved: boolean;
}

export function AuditLogs() {
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  useEffect(() => {
    loadSecurityAlerts();
  }, []);

  const loadSecurityAlerts = async () => {
    try {
      const response = await adminApi.getSecurityEvents();
      if (response.success && response.data) {
        setAlerts(response.data);
      } else {
        console.error('Failed to load alerts:', response.error);
      }
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  };

  const handleSecurityScan = async () => {
    toast.success('Security scan initiated');
    loadSecurityAlerts();
  };

  const getAlertBadgeClass = (type: string) => {
    switch (type) {
      case 'critical': return 'bg-red-500/20 text-red-400 border border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
      default: return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium tracking-tight text-foreground flex items-center gap-2">
          <Shield className="h-6 w-6 text-indigo-400" />
          Audit & Security
        </h1>
        <p className="text-sm text-muted-foreground">System-wide security events and audit trails</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-slate-900 border-slate-800 shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Security Events</CardTitle>
            <CardDescription className="text-muted-foreground">Recent critical and high-priority events</CardDescription>
          </CardHeader>
          <CardContent>
            {alerts.length > 0 ? (
              <div className="space-y-3">
                {alerts.map((alert, idx) => (
                  <div key={alert.id || idx} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 gap-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">{alert.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">{new Date(alert.timestamp).toLocaleString()}</p>
                    </div>
                    <Badge className={getAlertBadgeClass(alert.type)}>{alert.type}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-10 text-center">
                <AlertTriangle className="h-10 w-10 text-emerald-500/50 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-300">No recent security events</p>
                <p className="text-xs text-muted-foreground mt-1">Your system is secure and operating normally.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="bg-slate-900 border-slate-800 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-foreground">Security Controls</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <p className="text-sm font-medium text-foreground mb-1">Manual Security Scan</p>
                <p className="text-xs text-muted-foreground mb-4">Run a comprehensive check across all tenant environments.</p>
                <Button onClick={handleSecurityScan} className="w-full bg-indigo-600 hover:bg-indigo-700 text-foreground">
                  Run Full Scan
                </Button>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <p className="text-sm font-medium text-foreground mb-1">MFA Status</p>
                <p className="text-xs text-muted-foreground mb-4">Master Admin 2FA is currently {mfaEnabled ? 'enabled' : 'disabled'}.</p>
                <Button variant="outline" className="w-full border-slate-600 text-slate-300 hover:text-foreground hover:bg-slate-800">
                  Manage MFA
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
